// Shared meta + helpers for the Appointments module.
// -----------------------------------------------------------------------------

// Status metadata — chip colours and label
// Simplified in Feb 2026: only 3 operator-facing statuses.
export const STATUS_META = {
  scheduled: {
    label: "Scheduled",
    chip: "bg-[#a8dadc]/25 text-[#457b9d]",
    event: "#457b9d",    // Steel Blue
    tint: "#a8dadc",
  },
  completed: {
    label: "Completed",
    chip: "bg-[#12B76A]/12 text-[#0F9256]",
    event: "#12B76A",    // Green
    tint: "#D1FAE5",
  },
  cancelled: {
    label: "Cancelled",
    chip: "bg-[#e63946]/15 text-[#e63946]",
    event: "#e63946",    // Strawberry Red
    tint: "#FEE4E2",
  },
};

export const STATUS_ORDER = ["scheduled", "completed", "cancelled"];

// Fold legacy statuses (confirmed / checked_in / no_show / converted) that may
// still exist on old docs into the new 3-state vocabulary so the calendar,
// filters, and chips all render consistently.
const _LEGACY_STATUS_MAP = {
  confirmed: "scheduled",
  checked_in: "scheduled",
  no_show: "cancelled",
  converted: "completed",
};
export const normalizeStatus = (s) => _LEGACY_STATUS_MAP[s] || s || "scheduled";

export const SOURCE_LABELS = {
  manual: "Manual",
  phone: "Phone",
  walk_in: "Walk-in",
  website: "Website",
  whatsapp: "WhatsApp",
};

export const CALENDAR_DEFAULTS = {
  minHour: 8,
  maxHour: 20,
  slotMinutes: 30,
};

// ---------- Working hours ±2h guard ----------

// Default fallback when the garage hasn't set preferences yet — matches the
// backend factory defaults in /app/backend/routers/settings.py.
export const DEFAULT_WORKING_HOURS = { open: "09:00", close: "20:00" };
export const HOURS_SLACK_MIN = 120;   // ±2h tolerance

// HH:MM → minutes-since-midnight (returns null on garbage input).
const _hhmmToMin = (s) => {
  if (!s || typeof s !== "string" || !s.includes(":")) return null;
  const [h, m] = s.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
};

// Minutes → "HH:MM" for display in error messages.
const _minToHhmm = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * Compute the [earliest, latest] allowed booking window in "HH:MM" strings.
 * Falls back to 07:00 – 22:00 (matches 09:00 – 20:00 default ±2h).
 */
export const allowedWindow = (workingHours) => {
  const wh = { ...DEFAULT_WORKING_HOURS, ...(workingHours || {}) };
  const openM  = _hhmmToMin(wh.open)  ?? _hhmmToMin(DEFAULT_WORKING_HOURS.open);
  const closeM = _hhmmToMin(wh.close) ?? _hhmmToMin(DEFAULT_WORKING_HOURS.close);
  const lo = Math.max(0, openM - HOURS_SLACK_MIN);
  const hi = Math.min(24 * 60 - 1, closeM + HOURS_SLACK_MIN);
  return { earliest: _minToHhmm(lo), latest: _minToHhmm(hi), loMin: lo, hiMin: hi };
};

/**
 * True if the given Date's time-of-day sits within the ±2h window.
 * Ignores the date part entirely — only compares HH:MM.
 */
export const isWithinAllowedWindow = (dateOrIso, workingHours) => {
  const d = toDate(dateOrIso);
  if (!d) return false;
  const mins = d.getHours() * 60 + d.getMinutes();
  const { loMin, hiMin } = allowedWindow(workingHours);
  return mins >= loMin && mins <= hiMin;
};

// Format ISO date -> Date object; if input is naive (no zone) treat as local
export const toDate = (s) => {
  if (!s) return null;
  if (s instanceof Date) return s;
  return new Date(s);
};

// Serialise a Date -> naive ISO string (no timezone suffix). We store times as
// local wall-clock so a 10:00 Bengaluru appointment stays "10:00" for anyone
// looking at it later — no double-timezone conversion trap.
export const isoNaive = (d) => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
};

// Split minutes -> pretty duration ("1h 30m" / "45m")
export const prettyDuration = (mins) => {
  const n = Number(mins || 0);
  if (n === 0) return "0m";
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

export const fmtTime = (s) => {
  const d = toDate(s);
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

export const fmtDay = (s) => {
  const d = toDate(s);
  if (!d) return "—";
  return d.toLocaleDateString([], { weekday: "short", day: "2-digit", month: "short" });
};
